import React from 'react';
import {Avatar, CircularProgress} from '@mui/material';
import {Check, Close} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {fullSequence, statusToStepKey, Trade, TradeStatus} from '../../../../types/trades';

type StepperProps = {
    trade: Trade;
};

interface Step {
    key: TradeStatus;
    label: string;
}

const TradeStatusStepper: React.FC<StepperProps> = ({trade}) => {
    const {t} = useTranslation();

    const effectiveStatus = trade.effectiveTradeStatus;
    const currentIdx = fullSequence.indexOf(effectiveStatus);
    const isExpired = effectiveStatus.endsWith('_CANCELLED');

    const isCancelledStatus = effectiveStatus.endsWith('_CANCELLED');
    const isFailed = isExpired || isCancelledStatus;

    const steps: Step[] = [
        {key: 'WAITING_FOR_SELLER_CONFIRM', label: t('history.steps.WAITING_FOR_SELLER_CONFIRM')},
        {key: 'WAITING_FOR_SELLER_SEND', label: t('history.steps.WAITING_FOR_SELLER_SEND')},
        {key: 'WAITING_FOR_BUYER_ACCEPT', label: t('history.steps.WAITING_FOR_BUYER_ACCEPT')},
        {key: 'WAITING_FOR_VERIFICATION', label: t('history.steps.WAITING_FOR_VERIFICATION')},
    ];

    const getStepStatus = (stepKey: TradeStatus, index: number) => {
        const isCompleted = effectiveStatus === 'COMPLETED';
        const failedStepKey = statusToStepKey[effectiveStatus];

        // Finde den Index des Abbruchs
        const failureIndex = isFailed
            ? steps.findIndex(s => s.key === failedStepKey)
            : -1;

        const isPointOfFailure = isFailed && (
            stepKey === failedStepKey ||
            (failureIndex === -1 && index === currentIdx)
        );

        // 2. Ein Step VOR dem Fehler (Rote Umrandung + Graue Zahl)
        const isPastStepButFailed = isFailed && index < failureIndex;

        const isDone = isCompleted || (!isFailed && currentIdx > index);
        const isLoading = !isFailed && !isCompleted && currentIdx === index;

        // Linie rot einfärben bis zum Fehlerpunkt
        const isRedLine = isFailed && index <= failureIndex;
        const isBlueLine = !isFailed && (isCompleted || currentIdx >= index);

        return {
            isPointOfFailure,
            isPastStepButFailed,
            isDone,
            isLoading,
            isRedLine,
            isBlueLine
        };
    };

    return (
        <div className="flex items-center pb-6 mx-1.5">
            <div className="relative flex flex-col items-center">
                <Avatar
                    src={trade.sellerSteamAvatarUrl ?? ''}
                    className="ring-2 ring-gray-200 dark:ring-neutral-700 shadow-sm"
                />
                <span className="absolute top-full mt-2 text-[10px] text-gray-400 dark:text-gray-500 font-black uppercase tracking-widest">
                    {t('history.roles.seller')}
                </span>
            </div>

            <div className="flex items-center flex-1">
                {steps.map((step, i) => {
                    const {
                        isPointOfFailure,
                        isPastStepButFailed,
                        isDone,
                        isLoading,
                        isRedLine,
                        isBlueLine
                    } = getStepStatus(step.key, i);

                    return (
                        <React.Fragment key={step.key}>
                            <div className={`flex-1 h-[2px] mx-2 transition-colors duration-500 ${
                                isRedLine
                                    ? 'bg-red-500'
                                    : isBlueLine
                                        ? 'bg-dark-primary'
                                        : 'bg-gray-100 dark:bg-neutral-700'
                            }`}/>

                            <div className="relative flex flex-col items-center">
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-black transition-all duration-300 ${
                                    isPointOfFailure
                                        ? 'bg-red-600 text-white shadow-lg shadow-red-500/20'
                                        : isPastStepButFailed
                                            ? 'border-2 border-red-500 bg-transparent'
                                            : isDone
                                                ? 'bg-blue-500 dark:bg-dark-primary text-white shadow-md'
                                                : isLoading
                                                    ? 'border-2 border-transparent'
                                                    : 'border-2 border-gray-100 dark:border-neutral-700 text-gray-300 dark:text-neutral-500'
                                }`}>
                                    {isPointOfFailure ? (
                                        <Close fontSize="small" className="animate-in zoom-in duration-300"/>
                                    ) : isDone ? (
                                        <Check sx={{fontSize: 16}}/>
                                    ) : isLoading ? (
                                        <div className="relative flex items-center justify-center">
                                            <CircularProgress size={36} thickness={4} className="text-dark-primary"/>
                                            <span className="absolute text-dark-primary">{i + 1}</span>
                                        </div>
                                    ) : (
                                        <span className={isPastStepButFailed ? 'text-gray-400/60' : ''}>
                                            {i + 1}
                                        </span>
                                    )}
                                </div>

                                <span className={`absolute top-full mt-2 w-[120px] text-[9px] text-center left-1/2 -translate-x-1/2 font-bold uppercase tracking-tighter transition-colors ${
                                    isPointOfFailure
                                        ? 'text-red-500'
                                        : isPastStepButFailed
                                            ? 'text-red-500'
                                            : (isDone || isLoading)
                                                ? 'text-gray-700 dark:text-gray-200'
                                                : 'text-gray-400'
                                }`}>
                                    {step.label}
                                </span>
                            </div>
                        </React.Fragment>
                    );
                })}

                <div className={`flex-1 h-[2px] mx-2 ${
                    effectiveStatus === 'COMPLETED'
                        ? 'bg-dark-primary'
                        : 'bg-gray-100 dark:bg-neutral-700'
                }`}/>
            </div>

            <div className="relative flex flex-col items-center">
                <Avatar
                    src={trade.buyerSteamAvatarUrl ?? ''}
                    className="ring-2 ring-gray-200 dark:ring-neutral-700 shadow-sm"
                />
                <span className="absolute top-full mt-2 text-[10px] text-gray-400 dark:text-gray-500 font-black uppercase tracking-widest">
                    {t('history.roles.buyer')}
                </span>
            </div>
        </div>
    );
};

export default TradeStatusStepper;